import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles the Google OAuth redirect callback.
 * URL: /auth/callback/google?session_id=<emergent_session_id>
 *
 * Security:
 * - The session_id is validated server-side via the Emergent OAuth session endpoint.
 * - The backend verifies email_verified=true from Google.
 * - Only pre-provisioned emails can authenticate (no self-registration).
 * - Account takeover protection: if email maps to a different bound google_id → 409.
 */
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Verifying your Google identity…");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setError("Invalid callback — session ID missing. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        setStatus("Validating with Google…");
        const res = await fetch(`${API_URL}/api/auth/google/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await res.json();

        if (!res.ok) {
          // Surface the specific server error (account conflict, not registered, unverified email, etc.)
          setError(data.detail || "Google sign-in failed. Please try again.");
          return;
        }

        setStatus("Signed in. Redirecting…");
        await refreshUser();
        navigate("/", { replace: true });
      } catch {
        setError("Unable to complete sign-in. Please check your connection and try again.");
      }
    })();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F3F3]">
      <div className="bg-white border border-[#E5E5E5] rounded-lg p-10 max-w-sm w-full text-center" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        {!error ? (
          <>
            <Loader2 className="w-8 h-8 text-[#0176D3] mx-auto mb-4 animate-spin" />
            <p className="text-sm text-[#706E6B]" data-testid="google-callback-status">{status}</p>
          </>
        ) : (
          <>
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-4" />
            <p className="text-sm text-rose-600 mb-4" data-testid="google-callback-error">{error}</p>
            <button
              className="text-sm text-[#0176D3] hover:underline"
              onClick={() => navigate("/login")}
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
